import React, { useEffect, useRef, useState, useMemo } from 'react';
import countriesData from '../data/countries.json';
import { photoAlbums } from '../data/photoAlbums';
import blogList from '../data/blog/blog-list.json';
import { Link, useSearchParams } from 'react-router-dom';

const COUNTRY_ID_MAP = {
  France: 'france',
  Ireland: 'ireland',
  England: 'britain',
  Indonesia: 'java',
  'South Africa': 'south africa',
  'United States': 'usa',
  'United Arab Emirates': 'uae',
  'Sri Lanka': 'sri-lanka',
  'Hong Kong': 'hong-kong',
  'Czech Republic': 'czech-republic',
  'Northern Ireland': 'northern-ireland',
  'Vatican City': 'vatican',
  'Dominican Republic': 'dominican-republic',
  'Saint Kitts and Nevis': 'saint-kitts-and-nevis',
  'Saint Lucia': 'saint-lucia',
  'Sint Maarten': 'sint-maarten',
  'Isle of Man': 'isle-of-man',
  // Add more mappings as you add more countries to your JSON
};

function getCountryId(name) {
  // Lowercase and remove spaces for fallback
  return COUNTRY_ID_MAP[name] || name.toLowerCase().replace(/ /g, '');
}

// Monochrome SVG icons
const GlobeIcon = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
    <circle cx="10" cy="10" r="9" stroke={active ? '#fff' : '#333'} strokeWidth="2" fill="none" />
    <ellipse cx="10" cy="10" rx="6" ry="9" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" fill="none" />
    <ellipse cx="10" cy="10" rx="9" ry="3" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" fill="none" />
  </svg>
);
const TableIcon = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}>
    <rect x="2" y="2" width="16" height="16" rx="2" stroke={active ? '#fff' : '#333'} strokeWidth="2" fill="none" />
    <line x1="2" y1="8" x2="18" y2="8" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" />
    <line x1="2" y1="14" x2="18" y2="14" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" />
    <line x1="8" y1="2" x2="8" y2="18" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" />
    <line x1="14" y1="2" x2="14" y2="18" stroke={active ? '#fff' : '#333'} strokeWidth="1.5" />
  </svg>
);

const Map = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [svgContent, setSvgContent] = useState(null);
  const [popup, setPopup] = useState({ visible: false, x: 0, y: 0, country: null });

  // View mode from URL
  const view = searchParams.get('view') === 'table' ? 'table' : 'map';
  const setView = (v) => {
    const newParams = new URLSearchParams(searchParams);
    if (v === 'table') newParams.set('view', 'table');
    else newParams.delete('view');
    setSearchParams(newParams);
  };

  const svgRef = useRef();

  // Person filter
  const [person, setPerson] = useState('all'); // 'all', 'sonia', 'phil', 'alexa'

  // Cumulative filter
  const [showCumulative, setShowCumulative] = useState(true);

  // Unique filter
  const [showUnique, setShowUnique] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort function
  const sortCountries = (countries) => {
    return countries.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases for year fields
      if (sortField.includes('first_visited')) {
        aValue = aValue === 0 ? 9999 : (aValue || 9999); // Put 0 values at the end
        bValue = bValue === 0 ? 9999 : (bValue || 9999);
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Compute year range from data, depending on person
  const years = useMemo(() => {
    if (!countriesData.countries.length) return [0, new Date().getFullYear(), new Date().getFullYear()];
    let allYears;
    if (person === 'sonia') {
      allYears = countriesData.countries.map(c => c.sonia_first_visited).filter(y => y != null && y !== 0);
    } else if (person === 'phil') {
      allYears = countriesData.countries.map(c => c.phil_first_visited).filter(y => y != null && y !== 0);
    } else if (person === 'alexa') {
      allYears = countriesData.countries.map(c => c.alexa_first_visited).filter(y => y != null && y !== 0);
    } else {
      allYears = countriesData.countries.flatMap(c => [c.sonia_first_visited, c.phil_first_visited, c.alexa_first_visited]).filter(y => y != null && y !== 0);
    }
    const min = allYears.length ? Math.min(...allYears) : new Date().getFullYear();
    const max = new Date().getFullYear();
    // Build allowed slider values: 0, min, min+1, ..., max
    const allowed = [0];
    for (let y = min; y <= max; y++) allowed.push(y);
    return [0, max, min, allowed]; // [sliderMin, sliderMax, earliestNonzeroYear, allowedYears]
  }, [person]);
  const [selectedYear, setSelectedYear] = useState(years[1]);
  useEffect(() => {
    setSelectedYear(years[1]);
  }, [years]);

  // Filtered countries by year and person
  const filteredCountries = useMemo(() => {
    let filtered = countriesData.countries.filter(c => {
      let year = null;
      if (person === 'sonia') year = c.sonia_first_visited;
      else if (person === 'phil') year = c.phil_first_visited;
      else if (person === 'alexa') year = c.alexa_first_visited;
      else {
        // Show if any person visited by selected year
        const yearsArr = [c.sonia_first_visited, c.phil_first_visited, c.alexa_first_visited].filter(y => y != null);
        if (!yearsArr.length) return false;
        if (showCumulative) {
          return yearsArr.some(y => y <= selectedYear);
        } else {
          return yearsArr.some(y => y === selectedYear);
        }
      }
      if (year == null) return false;

      // Check if this person visited in the selected year range
      let personVisited = false;
      if (showCumulative) {
        personVisited = year <= selectedYear;
      } else {
        personVisited = year === selectedYear;
      }

      if (!personVisited) return false;

      // If showUnique is enabled, check that only this person visited
      if (showUnique && person !== 'all') {
        const otherPersons = [];
        if (person !== 'sonia') otherPersons.push(c.sonia_first_visited);
        if (person !== 'phil') otherPersons.push(c.phil_first_visited);
        if (person !== 'alexa') otherPersons.push(c.alexa_first_visited);

        // Check if any other person has visited (not null, but 0 is considered visited)
        const otherPersonsVisited = otherPersons.some(y => y != null);
        if (otherPersonsVisited) return false;
      }

      return true;
    });

    return sortCountries(filtered);
  }, [selectedYear, person, showCumulative, showUnique, sortField, sortDirection]);

  // Always re-render SVG when switching to map view or filteredCountries changes
  useEffect(() => {
    if (view !== 'map') return;
    setSvgContent(null); // Force reload
    fetch('/img/world.svg')
      .then(res => res.text())
      .then(setSvgContent);
  }, [view, filteredCountries]);

  useEffect(() => {
    if (!svgContent || !filteredCountries.length) return;
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElem = svgDoc.documentElement;
    // Color countries
    filteredCountries.forEach(country => {
      const id = getCountryId(country.name);
      const path = svgElem.querySelector(`#${id}`);
      if (path) {
        path.setAttribute('fill', '#B22816');
        path.style.cursor = 'pointer';
        // Add mouse events
        path.onmouseenter = e => {
          setPopup({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            country,
          });
        };
        path.onmousemove = e => {
          setPopup(p => ({ ...p, x: e.clientX, y: e.clientY }));
        };
        path.onmouseleave = () => {
          setPopup({ visible: false, x: 0, y: 0, country: null });
        };
      }
    });
    // Inline SVG
    if (svgRef.current) {
      svgRef.current.innerHTML = '';
      svgRef.current.appendChild(svgElem);
    }
  }, [svgContent, filteredCountries]);

  return (
    <section id="about">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 text-center">
            <h2>Map</h2>
            <hr className="star-primary" />
          </div>
        </div>
        {/* Person filter */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px 0 10px 0' }}>
          <span style={{ fontWeight: 'bold', marginRight: 10 }}>Person:</span>
          <button
            onClick={() => setPerson('all')}
            style={{
              background: person === 'all' ? '#B22816' : '#eee',
              color: person === 'all' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              marginRight: 6,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Any
          </button>
          <button
            onClick={() => setPerson('sonia')}
            style={{
              background: person === 'sonia' ? '#B22816' : '#eee',
              color: person === 'sonia' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              marginRight: 6,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Sonia
          </button>
          <button
            onClick={() => setPerson('phil')}
            style={{
              background: person === 'phil' ? '#B22816' : '#eee',
              color: person === 'phil' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              marginRight: 6,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Phil
          </button>
          <button
            onClick={() => setPerson('alexa')}
            style={{
              background: person === 'alexa' ? '#B22816' : '#eee',
              color: person === 'alexa' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Alexa
          </button>
          <div style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="unique"
              checked={showUnique}
              onChange={e => setShowUnique(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="unique" style={{ fontWeight: 'bold', margin: 0 }}>
              Show unique
            </label>
          </div>
        </div>
        {/* Year slider */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px 0 20px 0' }}>
          <span style={{ fontWeight: 'bold', marginRight: 10 }}>Year:</span>
          <input
            type="range"
            min={0}
            max={years[3].length - 1}
            value={years[3].indexOf(selectedYear)}
            onChange={e => setSelectedYear(years[3][Number(e.target.value)])}
            step={1}
            style={{ width: 200, marginRight: 10 }}
          />
          {selectedYear === 0 ? (
            <span style={{ minWidth: 120, fontWeight: 'bold', display: 'inline-block' }}>
              Unknown
            </span>
          ) : (
            <span style={{ minWidth: 120, fontWeight: 'bold', display: 'inline-block' }}>{selectedYear}</span>
          )}
          <div style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="cumulative"
              checked={showCumulative}
              onChange={e => setShowCumulative(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="cumulative" style={{ fontWeight: 'bold', margin: 0 }}>
              Show cumulative
            </label>
          </div>
        </div>
        <div style={{ textAlign: 'center', margin: '0 0 20px 0', fontWeight: 'bold', fontSize: 18 }}>
          Countries visited: {filteredCountries.length}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '10px 0 10px 0' }}>
          <span style={{ fontWeight: 'bold', marginRight: 10 }}>View:</span>
          <button
            onClick={() => setView('map')}
            style={{
              background: view === 'map' ? '#B22816' : '#eee',
              color: view === 'map' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              marginRight: 6,
              cursor: 'pointer',
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
            }}
            aria-label="Map view"
          >
            <GlobeIcon active={view === 'map'} />Map
          </button>
          <button
            onClick={() => setView('table')}
            style={{
              background: view === 'table' ? '#B22816' : '#eee',
              color: view === 'table' ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
            }}
            aria-label="Table view"
          >
            <TableIcon active={view === 'table'} />Table
          </button>
        </div>
        {view === 'map' ? (
          <div className="row" style={{ justifyContent: 'center' }}>
            <div className="col-xs-12 text-center">
              <div style={{ width: '100%', maxWidth: 950, margin: '0 auto', position: 'relative' }}>
                <div ref={svgRef} style={{ width: '100%' }} />
                {popup.visible && popup.country && (
                  <div
                    style={{
                      position: 'fixed',
                      left: popup.x + 10,
                      top: popup.y + 10,
                      background: 'rgba(255,255,255,0.95)',
                      border: '1px solid #B22816',
                      borderRadius: 6,
                      padding: 10,
                      zIndex: 1000,
                      pointerEvents: 'none',
                      minWidth: 180,
                    }}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: 16 }}>{popup.country.flag_emoji} {popup.country.name}</div>
                    <div><b>First visited:</b></div>
                    <ul style={{ paddingLeft: 18, margin: 0, fontSize: 16, textAlign: 'left' }}>
                      {popup.country.phil_first_visited != null && (
                        <li style={{ fontSize: 16 }}>Phil: {popup.country.phil_first_visited === 0 ? 'Unknown' : popup.country.phil_first_visited}</li>
                      )}
                      {popup.country.sonia_first_visited != null && (
                        <li style={{ fontSize: 16 }}>Sonia: {popup.country.sonia_first_visited === 0 ? 'Unknown' : popup.country.sonia_first_visited}</li>
                      )}
                      {popup.country.alexa_first_visited != null && (
                        <li style={{ fontSize: 16 }}>Alexa: {popup.country.alexa_first_visited === 0 ? 'Unknown' : popup.country.alexa_first_visited}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="row" style={{ justifyContent: 'center' }}>
            <div className="col-xs-12">
              <table className="table table-striped" style={{ width: '100%', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Flag</th>
                    <th
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Country {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => handleSort('continent')}
                    >
                      Continent {sortField === 'continent' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => handleSort('phil_first_visited')}
                    >
                      First Visited (Phil) {sortField === 'phil_first_visited' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => handleSort('sonia_first_visited')}
                    >
                      First Visited (Sonia) {sortField === 'sonia_first_visited' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => handleSort('alexa_first_visited')}
                    >
                      First Visited (Alexa) {sortField === 'alexa_first_visited' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th style={{ textAlign: 'left' }}>Photos</th>
                    <th style={{ textAlign: 'left' }}>Blogs</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCountries.map((country, idx) => {
                    const countryName = country.name.toLowerCase();
                    const photos = (photoAlbums || []).filter(album =>
                      (album.countries || []).some(c => c.toLowerCase() === countryName)
                    );
                    const blogs = (blogList || []).filter(post =>
                      !post.title.toLowerCase().includes('retrospective') &&
                      (post.countries || []).some(c => c.toLowerCase() === countryName)
                    );

                    return (
                      <tr key={country.name + idx}>
                        <td>{country.flag_emoji}</td>
                        <td>{country.name}</td>
                        <td>{country.continent}</td>
                        <td>{country.phil_first_visited === 0 ? 'Unknown' : country.phil_first_visited}</td>
                        <td>{country.sonia_first_visited === 0 ? 'Unknown' : country.sonia_first_visited}</td>
                        <td>{country.alexa_first_visited === 0 ? 'Unknown' : country.alexa_first_visited}</td>
                        <td>
                          {photos.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 16 }}>
                              {photos.map((album) => (
                                <li key={album.id} style={{ fontSize: 16 }}>
                                  <a href={album.url} target="_blank" rel="noopener noreferrer">
                                    {album.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                        <td>
                          {blogs.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 16 }}>
                              {blogs.map((post) => (
                                <li key={post.id} style={{ fontSize: 16 }}>
                                  <Link to={`/blog/${post.id}`}>
                                    {post.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Map; 